import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { IMAGES } from "@/data/content";

const HERO_BG = IMAGES.poolRoom;

const lines = ["A family-owned", "cafe in the heart", "of Footscray."];

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.1, ease: [0.19, 1, 0.22, 1], delay: 0.6 + i * 0.16 },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.45, 0.85]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex h-[100svh] w-full items-end overflow-hidden"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={HERO_BG} alt="Ethiopian coffee ceremony" className="h-full w-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 z-[1] bg-black" />
      <div className="absolute inset-0 z-[2] bg-gradient-to-t from-[var(--bg)] via-transparent to-black/30" />

      <div className="relative z-10 mx-auto w-full max-w-[1500px] px-6 pb-20 md:px-10 md:pb-28">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 1 }}
          className="mb-6 font-body text-xs uppercase tracking-[0.35em] text-accent"
        >
          Family-owned · Barkly Street, Footscray
        </motion.p>

        <h1 className="font-display text-5xl leading-[0.95] tracking-tight text-[var(--text)] sm:text-6xl lg:text-8xl">
          {lines.map((line, i) => (
            <span key={i} className="line-mask">
              <motion.span custom={i} variants={lineVariant} initial="hidden" animate="visible" className="block">
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="mt-10 flex flex-col items-start gap-6 sm:flex-row sm:items-center"
        >
          <p className="max-w-md font-serif-c text-xl leading-relaxed text-[var(--text)]/80">
            A warm, family-run neighbourhood cafe where good coffee, honest food and familiar faces have made us a Footscray local.
          </p>
          <div className="flex items-center gap-2 text-[var(--text)]/60">
            <ArrowDown size={16} className="animate-bounce" />
            <span className="font-body text-xs uppercase tracking-[0.25em]">Scroll to enter</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
